import { deleteField } from "firebase/firestore";
import { normalizeAllergenCodes } from "../../nutrition/foods/foodClassification";

const PROFILE_FIELD_MAPPINGS = [
  { settingsKey: "name_for_coach", propertyKey: "nameForCoach" },
  { settingsKey: "surname_for_coach", propertyKey: "surnameForCoach" },
  { settingsKey: "notes", propertyKey: "notes" },
  { settingsKey: "phone", propertyKey: "phone" },
  { settingsKey: "gender", propertyKey: "gender" },
  { settingsKey: "profile_squat", propertyKey: "profileSquat" },
  { settingsKey: "profile_bench_press", propertyKey: "profileBenchPress" },
  { settingsKey: "profile_deadlift", propertyKey: "profileDeadlift" },
  { settingsKey: "dietary_preference", propertyKey: "dietaryPreference" },
  {
    settingsKey: "allergen_codes",
    propertyKey: "allergenCodes",
    defaultValue: [],
    normalizeValue: normalizeAllergenCodes,
  },
  {
    settingsKey: "show_nutrition_plan_totals_override",
    propertyKey: "showNutritionPlanTotalsOverride",
    defaultValue: null,
  },
];

const isValidClient = (client) => !!client && typeof client === "object";

const cloneIfNeeded = (value) => (Array.isArray(value) ? [...value] : value);

const resolveComparableValue = (value, defaultValue, normalizeValue) => {
  const resolvedValue =
    value === undefined || value === null ? defaultValue : value;

  if (typeof normalizeValue === "function") {
    return cloneIfNeeded(normalizeValue(resolvedValue));
  }

  return cloneIfNeeded(resolvedValue);
};

const areArraysEqual = (left, right) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

const areDifferent = (left, right, defaultValue, normalizeValue) => {
  const normalizedLeft = resolveComparableValue(left, defaultValue, normalizeValue);
  const normalizedRight = resolveComparableValue(right, defaultValue, normalizeValue);

  if (Array.isArray(normalizedLeft) || Array.isArray(normalizedRight)) {
    return !areArraysEqual(
      Array.isArray(normalizedLeft) ? normalizedLeft : [],
      Array.isArray(normalizedRight) ? normalizedRight : []
    );
  }

  return normalizedLeft !== normalizedRight;
};

export const buildClientProfileSavePayload = (settings = {}) => {
  const payload = { ...settings };

  if (Object.prototype.hasOwnProperty.call(payload, "allergen_codes")) {
    payload.allergen_codes = normalizeAllergenCodes(payload.allergen_codes);
  }

  if (
    Object.prototype.hasOwnProperty.call(
      payload,
      "show_nutrition_plan_totals_override"
    ) &&
    (payload.show_nutrition_plan_totals_override === undefined ||
      payload.show_nutrition_plan_totals_override === null)
  ) {
    payload.show_nutrition_plan_totals_override = deleteField();
  }

  return payload;
};

export const syncClientProfileDraftWithPersistedData = (client) => {
  if (!isValidClient(client)) {
    return;
  }

  PROFILE_FIELD_MAPPINGS.forEach(
    ({ settingsKey, propertyKey, defaultValue, normalizeValue }) => {
      client.setSettings(
        settingsKey,
        resolveComparableValue(client[propertyKey], defaultValue, normalizeValue)
      );
    }
  );
};

export const hasClientProfileUnsavedChanges = (client) => {
  if (!isValidClient(client)) {
    return false;
  }

  const settings = client.clientSettings ?? {};
  return PROFILE_FIELD_MAPPINGS.some(
    ({ settingsKey, propertyKey, defaultValue, normalizeValue }) =>
      areDifferent(
        settings[settingsKey],
        client[propertyKey],
        defaultValue,
        normalizeValue
      )
  );
};

export const saveClientProfileDraft = (client) => {
  if (!isValidClient(client) || client.isOffline) {
    return false;
  }

  const settings = client.clientSettings ?? {};
  let hasChanges = false;

  PROFILE_FIELD_MAPPINGS.forEach(
    ({ settingsKey, propertyKey, defaultValue, normalizeValue }) => {
      const nextValue = resolveComparableValue(
        settings[settingsKey],
        defaultValue,
        normalizeValue
      );

      if (
        areDifferent(nextValue, client[propertyKey], defaultValue, normalizeValue)
      ) {
        client.setProperty(propertyKey, nextValue);
        hasChanges = true;
      }
    }
  );

  if (hasChanges) {
    client.saveClientProfile(buildClientProfileSavePayload(settings));
  }

  return hasChanges;
};

export const discardClientProfileDraft = (client) => {
  syncClientProfileDraftWithPersistedData(client);
};

export { PROFILE_FIELD_MAPPINGS };
